import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { getCurrentProject } from "../../../../actions/projectsActions";
import { updateCurrentProjectService } from "../../../../actions/servicesActions";
import { resetProjectsState } from "../../../../slices/projectsSlice";

const ServiceDetails = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const project = useSelector((state) => state.projects.current);
  const service = project?.service;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    enabled: false,
    // Scheduler
    schedulerEnabled: false,
    schedulerCron: "",
    schedulerTimezone: "America/Mexico_City",
    // Rules
    periodType: "quarterly",
    periodDayOfMonth: 1,
    periodChargeType: "start",
    formulaType: "percentage",
    formulaValue: 0,
    // Auto Approval
    autoApprovalEnabled: false,
    // Notifications
    notifyAdminOnCompletion: false,
    notifyAssociateOnApproval: false,
    adminEmails: "",
  });

  useEffect(() => {
    dispatch(getCurrentProject({ id: projectId }));
    return () => dispatch(resetProjectsState());
  }, [projectId]);

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name || "",
        description: service.description || "",
        enabled: service.enabled || false,
        // Scheduler
        schedulerEnabled: service.scheduler?.enabled || false,
        schedulerCron: service.scheduler?.cron || "",
        schedulerTimezone: service.scheduler?.timezone || "America/Mexico_City",
        // Rules
        periodType: service.rules?.period?.type || "quarterly",
        periodDayOfMonth: service.rules?.period?.dayOfMonth || 1,
        periodChargeType: service.rules?.period?.chargeType || "start",
        formulaType: service.rules?.formula?.type || "percentage",
        formulaValue: service.rules?.formula?.value || 0,
        // Auto Approval
        autoApprovalEnabled: service.autoApproval?.enabled || false,
        // Notifications
        notifyAdminOnCompletion:
          service.notifications?.notifyAdminOnCompletion || false,
        notifyAssociateOnApproval:
          service.notifications?.notifyAssociateOnApproval || false,
        adminEmails: service.notifications?.adminEmails?.join(", ") || "",
      });
    }
  }, [service]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSwitchChange = (name) => (e) => {
    setFormData({
      ...formData,
      [name]: e.target.checked,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!service?._id) return;

    const servicePayload = {
      name: formData.name,
      description: formData.description,
      enabled: formData.enabled,
      scheduler: {
        enabled: formData.schedulerEnabled,
      },
      rules: {
        period: {
          type: formData.periodType,
          dayOfMonth: Number(formData.periodDayOfMonth),
          chargeType: formData.periodChargeType,
        },
        formula: {
          type: formData.formulaType,
          value: Number(formData.formulaValue),
        },
      },
      autoApproval: {
        enabled: formData.autoApprovalEnabled,
      },
      notifications: {
        notifyAdminOnCompletion: formData.notifyAdminOnCompletion,
        notifyAssociateOnApproval: formData.notifyAssociateOnApproval,
        adminEmails: formData.adminEmails
          .split(",")
          .map((email) => email.trim())
          .filter((email) => email),
      },
    };

    dispatch(
      updateCurrentProjectService({
        projectId,
        serviceId: service._id,
        service: servicePayload,
        callback: () => {
          dispatch(getCurrentProject({ id: projectId }));
        },
      })
    );
  };

  const handleBack = () => {
    navigate("/dashboard/servicios");
  };

  if (!project || !service) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Configuración del servicio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Proyecto: {project.name}
          </Typography>
        </Box>
      </Box>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        {/* Información General */}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Información general
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre del servicio"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={handleSwitchChange("enabled")}
                />
              }
              label="Servicio habilitado"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Scheduler */}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Scheduler (Programación)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.schedulerEnabled}
                  onChange={handleSwitchChange("schedulerEnabled")}
                />
              }
              label="Scheduler habilitado"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Cron:{" "}
              <strong>{formData.schedulerCron || "No configurado"}</strong>
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Zona horaria: <strong>{formData.schedulerTimezone}</strong>
            </Typography>
          </Grid>
        </Grid>
        {service?.scheduler?.cron &&
          (() => {
            const cronParts = service.scheduler.cron.split(" ");
            const cronDay = cronParts[2];
            const serviceDayOfMonth = service.rules?.period?.dayOfMonth;
            if (
              cronDay &&
              cronDay !== "*" &&
              serviceDayOfMonth &&
              Number(cronDay) !== serviceDayOfMonth
            ) {
              return (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Inconsistencia detectada: El cron está configurado para el día{" "}
                  {cronDay}, pero las reglas indican día {serviceDayOfMonth}.
                  Esto puede causar comportamiento inesperado.
                </Alert>
              );
            }
            return null;
          })()}

        <Divider sx={{ my: 3 }} />

        {/* Reglas */}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Reglas de cálculo
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth>
              <InputLabel>Tipo de período</InputLabel>
              <Select
                name="periodType"
                value={formData.periodType}
                onChange={handleChange}
                label="Tipo de período"
              >
                <MenuItem value="daily" disabled>
                  Diario
                </MenuItem>
                <MenuItem value="monthly" disabled>
                  Mensual
                </MenuItem>
                <MenuItem value="quarterly">Trimestral</MenuItem>
                <MenuItem value="annual" disabled>
                  Anual
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth>
              <InputLabel>Día del Mes</InputLabel>
              <Select
                name="periodDayOfMonth"
                value={formData.periodDayOfMonth}
                onChange={handleChange}
                label="Día del mes"
                MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
              >
                {[...Array(31)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Cargo</InputLabel>
              <Select
                name="periodChargeType"
                value={formData.periodChargeType}
                onChange={handleChange}
                label="Tipo de cargo"
              >
                <MenuItem value="start">Inicio del período</MenuItem>
                <MenuItem value="end">Fin del período</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} md={2.4}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Fórmula</InputLabel>
              <Select
                name="formulaType"
                value={formData.formulaType}
                onChange={handleChange}
                label="Tipo de Fórmula"
              >
                <MenuItem value="percentage">Porcentaje</MenuItem>
                <MenuItem value="fixed_amount">Monto Fijo</MenuItem>
                <MenuItem value="progressive">Progresivo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <TextField
              label="Valor"
              name="formulaValue"
              type="number"
              value={formData.formulaValue}
              onChange={handleChange}
              fullWidth
              inputProps={{ step: 0.01 }}
            />
          </Grid> */}
        </Grid>
        {formData.periodDayOfMonth >= 29 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {formData.periodDayOfMonth === 29 && (
              <>
                El día 29 no existe en febrero (excepto años bisiestos). El
                cálculo no se ejecutará en ese mes.
              </>
            )}
            {formData.periodDayOfMonth === 30 && (
              <>
                El día 30 no existe en febrero. El cálculo no se ejecutará en
                ese mes.
              </>
            )}
            {formData.periodDayOfMonth === 31 && (
              <>
                El día 31 no existe en febrero, abril, junio, septiembre y
                noviembre. El cálculo no se ejecutará en esos meses.
              </>
            )}
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Aprobación y Notificaciones */}
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Aprobación y Notificaciones
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.autoApprovalEnabled}
                  onChange={handleSwitchChange("autoApprovalEnabled")}
                />
              }
              label="Auto aprobación"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.notifyAdminOnCompletion}
                  onChange={handleSwitchChange("notifyAdminOnCompletion")}
                />
              }
              label="Notificar admin"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  disabled
                  checked={formData.notifyAssociateOnApproval}
                  onChange={handleSwitchChange("notifyAssociateOnApproval")}
                />
              }
              label="Notificar asociado"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Emails de Administradores (separados por coma)"
              name="adminEmails"
              value={formData.adminEmails}
              onChange={handleChange}
              fullWidth
              placeholder="admin1@example.com, admin2@example.com"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="text" onClick={handleBack}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Guardar cambios
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ServiceDetails;
